/**
 * Debug del socket / WebRTC de Voice Platform.
 *
 * Desarrollo (`ENV.ENVIRONMENT === 'development'`): siempre activo.
 *
 * Producción / staging — en la consola del navegador:
 *   window.enableMercatelyVoiceDebug()
 * o:
 *   window.__MERCATELY_VOICE_SOCKET_DEBUG__ = true
 *
 * El flag se guarda en localStorage y sobrevive al reload.
 * Se limpia con logout / cambio de retailer (localStorage.clear) o:
 *   window.disableMercatelyVoiceDebug()
 *
 * Tip si no ves actividad:
 *   window.explainVoiceCallsSetup()
 */
export const VOICE_SOCKET_DEBUG_GLOBAL_KEY = '__MERCATELY_VOICE_SOCKET_DEBUG__';
export const VOICE_SOCKET_DEBUG_STORAGE_KEY = 'mercately_voice_socket_debug';

const LOG_PREFIX = '[VoiceSocket]';

function isRailsDevelopmentEnvironment() {
  return typeof ENV !== 'undefined' && ENV.ENVIRONMENT === 'development';
}

/** @returns {string|undefined} */
export function getAppEnvironment() {
  if (typeof ENV !== 'undefined' && ENV.ENVIRONMENT) {
    return String(ENV.ENVIRONMENT);
  }
  return undefined;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isVoiceDebugFlagTruthy(value) {
  return value === true
    || value === 1
    || value === '1'
    || (typeof value === 'string' && value.trim().toLowerCase() === 'true');
}

function readPersistedVoiceDebugFlag() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return isVoiceDebugFlagTruthy(window.localStorage.getItem(VOICE_SOCKET_DEBUG_STORAGE_KEY));
  } catch {
    return false;
  }
}

function persistVoiceDebugFlag(enabled) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (enabled) {
      window.localStorage.setItem(VOICE_SOCKET_DEBUG_STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(VOICE_SOCKET_DEBUG_STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode
  }
}

export function isVoiceSocketDebugEnabled() {
  if (isRailsDevelopmentEnvironment()) {
    return true;
  }

  if (typeof window !== 'undefined' && isVoiceDebugFlagTruthy(window[VOICE_SOCKET_DEBUG_GLOBAL_KEY])) {
    return true;
  }

  return readPersistedVoiceDebugFlag();
}

/**
 * @param {boolean} [enabled=true]
 * @returns {boolean}
 */
export function setVoiceSocketDebugEnabled(enabled = true) {
  const nextEnabled = Boolean(enabled);
  if (typeof window !== 'undefined') {
    window[VOICE_SOCKET_DEBUG_GLOBAL_KEY] = nextEnabled;
  }
  persistVoiceDebugFlag(nextEnabled);

  // Always acknowledge — not gated — so the engineer knows the flag stuck.
  // eslint-disable-next-line no-console
  console.log(
    LOG_PREFIX,
    nextEnabled
      ? `debug ON (persisted, env=${getAppEnvironment() ?? 'unknown'}). Logs nuevos al conectar/llamar. Tip: window.explainVoiceCallsSetup()`
      : 'debug OFF (cleared from localStorage)'
  );

  return nextEnabled;
}

/**
 * @param {...unknown} args
 */
export function voiceSocketLog(...args) {
  if (!isVoiceSocketDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(LOG_PREFIX, ...args);
}

/**
 * @param {...unknown} args
 */
export function voiceSocketWarn(...args) {
  if (!isVoiceSocketDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.warn(LOG_PREFIX, ...args);
}

/**
 * @param {...unknown} args
 */
export function voiceSocketError(...args) {
  if (!isVoiceSocketDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.error(LOG_PREFIX, ...args);
}

if (typeof window !== 'undefined') {
  // Restore window flag early so reload keeps debug without re-calling enable.
  if (readPersistedVoiceDebugFlag()) {
    window[VOICE_SOCKET_DEBUG_GLOBAL_KEY] = true;
  }

  window.enableMercatelyVoiceDebug = () => setVoiceSocketDebugEnabled(true);
  window.disableMercatelyVoiceDebug = () => setVoiceSocketDebugEnabled(false);
}
